import { Empty } from '@/components/ui/EmptyState';
import { YStack } from '@/components/ui/layout/layout';
import { Page } from '@/design-system-v2/page';
import { cn } from '@/lib/utils';

import { AgentNotes } from './components/agent-notes';
import { PersonFacts } from './components/person-facts';
import { PersonHeader } from './components/person-header';
import { WikiBackLink } from './components/wiki-back-link';
import {
  AccountsEditor,
  AddFact,
  AliasesEditor,
  NameEditor,
  PersonMenu,
  UserNotesEditor,
  useRemoveFactMutation,
} from './edits';
import { usePersonQuery } from './people-wiki.query';

export const PersonPage = ({
  personId,
  startRenaming = false,
}: {
  personId: string;
  /** Open with the name ready to be typed over, as a page that was just created does. */
  startRenaming?: boolean;
}) => {
  const { data: person, isLoading } = usePersonQuery(personId);
  const { mutate: removeFact } = useRemoveFactMutation(personId);

  // A failed background refetch must not hide a page that already has data.
  let body;
  if (person) {
    body = (
      <>
        <PersonHeader
          person={person}
          nameEditor={
            <NameEditor person={person} startRenaming={startRenaming} />
          }
          aliases={<AliasesEditor person={person} />}
          accounts={<AccountsEditor person={person} />}
          actions={<PersonMenu person={person} />}
        />
        <UserNotesEditor personId={personId} savedNotes={person.userNotes} />
        <AgentNotes text={person.agentNotes} />
        <PersonFacts
          facts={person.facts}
          lookup={person.lookup}
          onRemoveFact={removeFact}
          addFact={<AddFact personId={personId} />}
        />
      </>
    );
  } else if (isLoading) {
    body = <span className="text-sm text-foreground-muted">Loading…</span>;
  } else {
    body = (
      <Empty.Root variant="compact">
        <Empty.Title>Couldn't load this person</Empty.Title>
      </Empty.Root>
    );
  }

  return (
    <YStack className="h-full overflow-y-auto">
      <YStack
        className={cn(
          Page.column({ width: 'reading', top: true }),
          'gap-6 pb-8'
        )}
      >
        <WikiBackLink />
        {body}
      </YStack>
    </YStack>
  );
};
